import Logger from '../logging/logger';
import { defaultFillStyle, defaultOutlineStyle } from './styles';

import {
    Coast,
    District,
    Entity,
    Island,
    Municipality,
    Neighbourhood,
    Province,
    Region,
} from '../locations';

const Property = 'properties';
const Radius = 'radius';
const RadiusClose = 'radius-close';

const layers = [
    Region,
    Province,
    Coast,
    Island,
    Municipality,
    District,
    Neighbourhood,
    Entity,
    Property,
    Radius,
    RadiusClose,
];

const locationSources = [
    Region,
    Province,
    Municipality,
    District,
    Neighbourhood,
    Entity,
];

const defaultFillLayout = {
    type: 'fill',
    layout: {
        visibility: 'visible',
        'fill-sort-key': [
            '-',
            [
                'coalesce',
                [ 'get', 'area' ],
                0,
            ],
        ],
    },
    state: {
        hover: false,
    },
    paint: defaultFillStyle,
};

const defaultOutlineLayout = {
    type: 'line',
    layout: {
        visibility: 'visible',
        'line-sort-key': [
            '-',
            [
                'coalesce',
                [ 'get', 'area' ],
                0,
            ],
        ],
    },
    paint: defaultOutlineStyle,

};

function buildFillLayer( layerName, source, layer ) {
    return Object.assign( {}, defaultFillLayout, layer, {
        id: layerName + ':fill',
        source: source,
    } );
}

function buildOutlineLayer( layerName, source, layer ) {
    return Object.assign( {}, defaultOutlineLayout, layer, {
        id: layerName + ':outline',
        source: source,
    } );
}

/**
 * findFirstCustomLayer returns the ID of the first custom layer added to the map
 * @param {mapboxgl.Map} ctx - the map context to use
 * @returns an ID of the first custom layer
 */
function findFirstCustomLayer( ctx ) {
    const mapLayers = ctx.getStyle().layers;
    let id;
    for ( let i = 0; i < mapLayers.length; i++ ) {
        if ( layers.includes( mapLayers[ i ].source ) ) {
            id = mapLayers[ i ].id;
            break;
        }
    }
    return id;
}

/**
 * getFirstSymbolLayerID finds the ID the first symbol layer in the maps layer stack
 * @param {mapboxgl.Map} ctx - the map context to use
 * @returns an ID of the first symbol layer
 */
function getFirstSymbolLayerID( ctx ) {
    const layers = ctx.getStyle().layers;
    let id;
    for ( let i = 0; i < layers.length; i++ ) {
        if ( layers[ i ].type === 'symbol' ) {
            id = layers[ i ].id;
            break;
        }
    }
    return id;
}

/**
 * removeApproxLocationCircle Removes the approximate location circle from the given map context
 * @param {mapboxgl.Map} ctx - the map context to use
 */
function removeApproxLocationCircle( ctx ) {
    if ( ctx.getLayer( 'approx-location:fill' ) ) {
        ctx.removeLayer( 'approx-location:fill' );
    }
    if ( ctx.getSource( 'approx-location' ) ) {
        ctx.removeSource( 'approx-location' );
    }
}

function updatePropertyIcons( ctx ) {
    let propertyLayout = [ 'case' ];

    ctx.properties.forEach( ( property ) => {
        propertyLayout.push(
            [
                'boolean',
                [ '==', [ 'get', 'id' ], property.id ],
                false,
            ],
            property.getIcon(),
        );
    } );

    propertyLayout.push( [ 'get', 'icon' ] );

    if ( ctx.map.getLayer( 'properties' ) ) {
        ctx.map.setLayoutProperty( 'properties', 'icon-image',
            propertyLayout,
            {
                validate: ctx.debug,
            },
        );
    }
}

/**
 * removeLayersForSource removes all map layers for a particular source from the map
 * @param {mapbox.Map}
 * @param {String} sourceID the ID of the source to remove from the map
 * @returns void
 */
function removeLayersForSource( ctx, sourceID ) {
    if ( ctx.getLayer( sourceID + ':fill' ) ) {
        ctx.removeLayer( sourceID + ':fill' );
    }

    if ( ctx.getLayer( sourceID + ':outline' ) ) {
        ctx.removeLayer( sourceID + ':outline' );
    }

    // Some source do not have fill/outline layers
    if ( ctx.getLayer( sourceID ) ) {
        ctx.removeLayer( sourceID );
    }
}

/**
 * @param {Object} options the options to use for adding the layer to the map
 * @returns void
 */
function setLayer( ctx, options = {} ) {
    options = Object.assign( {}, {
        mapOptions: {},
        fillLayer: {},
        outlineLayer: {},
        beforeLayerID: null,
        filters: [],
    }, options );

    if ( !options.beforeLayerID ) {
        const layers = ctx.map.getStyle().layers.map( ( layer, index ) => {
            return {
                id: layer.id,
                index: index,
            };
        } );

        const propertyLayer = layers.find( ( layer ) => {
            return layer.id === Property;
        } );

        let beforeID = null;

        const ls = {
            [ Province ]: [ Municipality ],
            [ Municipality ]: [ District, Neighbourhood, Entity, Property, Province ],
            [ District ]: [ Neighbourhood, Entity, Property ],
            [ Neighbourhood ]: [ Entity, Property ],
            [ Entity ]: [ Property ],
        };

        let priorityLayers = ls[ options.source ];

        if ( priorityLayers ) {
            priorityLayers = priorityLayers.map( ( l ) => l + ':fill' );

            let priorityLayersFound = layers.filter( ( l ) => priorityLayers.includes( l.id ) );

            priorityLayersFound = priorityLayersFound.map( ( l ) => l.index );

            priorityLayersFound = priorityLayersFound.sort( ( a, b ) => a - b );

            const targetLayer = layers.find( ( l ) => l.index === priorityLayersFound[ 0 ] );

            if ( targetLayer ) {
                beforeID = targetLayer.id;
            }
        }

        if ( ( propertyLayer && beforeID > propertyLayer.index ) || ( !beforeID && propertyLayer ) ) {
            beforeID = propertyLayer.id;
        }

        options.beforeLayerID = beforeID;
    }

    if ( options.source && options.name ) {
        const fillLayer = buildFillLayer( options.name, options.source, options.fillLayer );

        Logger.info( '[map] Adding Map Fill Layer with ID: ' + options.name, fillLayer );

        ctx.map.addLayer( fillLayer, options.beforeLayerID );

        const outlineLayer = buildOutlineLayer( options.name, options.source, options.outlineLayer );

        Logger.info( '[map] Adding Map Outline Layer with ID: ' + options.name, outlineLayer );

        ctx.map.addLayer( outlineLayer, options.beforeLayerID );
    }

    if ( options.filters.length > 0 ) {
        for ( let f = 0; f < options.filters.length; f++ ) {
            ctx.map.setFilter( options.filters[ f ].layer, options.filters[ f ].filter, ctx.debug );
        }
    }

    if ( options.mapOptions.maxZoom ) {
        ctx.map.setMaxZoom( options.mapOptions.maxZoom );
    }

    if ( options.mapOptions.zoom ) {
        ctx.map.setZoom( options.mapOptions.zoom );
    }
}

function filterLayerIfExists( ctx, layerName, filter, validate = false ) {
    const layer = ctx.getLayer( layerName );

    if ( !layer ) {
        return false;
    }

    ctx.setFilter( layerName, filter, {
        validate: validate,
    } );

    return true;
}

/**
 * @param {mapboxgl.Map} ctx - the map context to use
 * @param {String} sourceName - the name of the source
 */
function deactiveFeaturesForSource( ctx, sourceName ) {
    const source = ctx.getSource( sourceName );

    if ( !source ) {
        return false;
    }

    const features = source.serialize().data.features || [];

    if ( features.length === 0 ) {
        return false;
    }

    const state = {
        active: false,
    };

    for ( let e = 0; e < features.length; e++ ) {
        const filter = {
            source: sourceName,
            id: features[ e ].id,
        };

        ctx.setFeatureState( filter, state );
    }

    return true;
}

function setFeatureStateIfSourceExists( ctx, filter, state ) {
    const source = ctx.getSource( filter.source );

    if ( !source ) {
        return false;
    }

    ctx.setFeatureState( filter, state );

    return true;
}

/**
 * @param {mapboxgl.Map} ctx - the map context to use
 */
function copyCustomLayersAndSources( ctx ) {
    const currentStyle = ctx.getStyle();

    const customSources = [
        ...locationSources,
        'radius',
        'area',
        'radius-close',
        'area-close',
        'properties',

    ];

    const sourcesToCopy = {};

    for ( const sourceName in currentStyle.sources ) {
        if ( customSources.includes( sourceName ) ) {
            sourcesToCopy[ sourceName ] = currentStyle.sources[ sourceName ];
        }
    }

    const layersToCopy = currentStyle.layers.filter( ( layer ) => {
        return customSources.includes( layer.source );
    } );

    return {
        layers: layersToCopy,
        sources: sourcesToCopy,
    };
}

export {
    Property,
    Radius,
    locationSources,
    buildFillLayer,
    buildOutlineLayer,
    findFirstCustomLayer,
    getFirstSymbolLayerID,
    removeApproxLocationCircle,
    updatePropertyIcons,
    removeLayersForSource,
    setLayer,
    filterLayerIfExists,
    deactiveFeaturesForSource,
    setFeatureStateIfSourceExists,
    copyCustomLayersAndSources,
};

export default layers;
